import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        startOrder: 'Start Order',
        reviewOrder: 'Review Order',
        home: 'Home',
        menu: 'Menu',
        about: 'About',
        gallery: 'Gallery',
        contact: 'Contact',
        location: 'Location',
        reservations: 'Reservations',
        order: 'Order Online',
        cart: 'Cart',
        language: 'Language',
        profile: 'Profile',
        login: 'Login',
        logout: 'Logout',
        admin: 'Admin'
      },
      
      // Home page
      home: {
        hero: {
          orderNow: 'Order Now',
          viewMenu: 'View Menu',
          viewMokalandMenu: 'View Moka Land Menu',
          eyebrow: 'Fountain Square 8 · Baku',
          titleLine1: 'Fountain Square,',
          titleLine2: 'since 1995.',
          sub: 'A small story in a big city.',
          seeMenu: 'See the menu →',
          hours: 'Open now · 08:00–03:00',
          location: 'Fountain Square 8, Baku'
        },
        highlights: {
          awardWinning: 'Award-Winning',
          awardDesc: '4.0★ rating from 692+ satisfied customers',
          freshDaily: 'Fresh Daily',
          freshDailyDesc: 'Made fresh daily with premium ingredients',
          fastDelivery: 'Fast Delivery',
          fastDeliveryDesc: 'Quick delivery across Baku neighborhoods'
        },
        businessInfo: {
          hours: 'Hours:',
          hoursText: 'Daily 8:00 AM - 3:00 AM',
          phone: 'Phone:',
          phoneNumber: '*9777'
        },
        cta: {
          title: 'Craving Authentic Pizza?',
          subtitle: 'Order now for delivery across Baku or visit us at Fountain Square. Open until 3:00 AM for your late-night cravings!',
          order: 'Order Online',
          menu: 'View Menu'
        },
        heritage: {
          since: 'Since',
          quote: 'The rich heritage of our national cuisine, and the finest flavours of world cuisine. A story that continues at every table, with years of experience and refined taste.',
        },
        stats: {
          years: 'Years in Baku',
          hours: 'Open Every Day',
          locationValue: 'Fountain Square',
          location: 'Baku, Azerbaijan',
        },
        instagram: {
          label: 'Follow Along',
          title: '@pizzainnbaku',
          body: 'Follow us for daily specials, seasonal dishes and moments from the kitchen.',
          follow: 'Follow on Instagram',
        },
        delivery: {
          orderVia: 'Delivery via',
          orderDirect: 'Order Direct',
        },
        chapters: {
          eyebrow: 'Pizza Inn',
          title: 'In motion',
          contentsLabel: 'Contents',
          menuCta: 'See the whole menu',
          pizza: {
            name: 'Pizza',
            eyebrow: 'The house',
            subject: 'Made from scratch',
            alt: 'Pizza being made by hand at Pizza Inn',
            gloss: 'The oven, the flour, and the peel.',
          },
          breakfast: {
            name: 'Breakfast',
            eyebrow: 'Morning',
            subject: 'Breakfast, served',
            alt: 'Breakfast being served at Pizza Inn',
            gloss: 'Eggs, bread, and the first table of the day.',
          },
          coffee: {
            name: 'Coffee',
            eyebrow: 'The bar',
            subject: 'Coffee and a croissant',
            alt: 'Coffee and a croissant at Pizza Inn',
            gloss: 'Small cups and long afternoons.',
          },
          grill: {
            name: 'The Grill',
            eyebrow: 'Evening',
            subject: 'From the grill',
            alt: 'Steak being cooked at Pizza Inn',
            gloss: 'Coals, a tandoor, and cuts made for sharing.',
          },
          garden: {
            name: 'The Garden',
            eyebrow: 'Dusk',
            subject: 'The Garden',
            alt: 'The Pizza Inn Garden, inside and out',
            gloss: 'The room with no roof.',
            body: 'Native flavours, a generous table. The most loved tastes of Azerbaijani cuisine, in the Garden.',
            cta: 'Visit the Garden',
          },
          mokaland: {
            name: 'Moka Land',
            eyebrow: 'For the family',
            subject: 'The sweetest taste of fun',
            alt: 'Children playing and eating at Moka Land',
            gloss: 'The rope, the hoops, and a table for everyone.',
            body: 'The sweetest taste of fun is at Moka Land! Play for the kids, delight for the whole family.',
            cta: 'Visit Moka Land',
          },
        },
      },

      // About page
      about: {
        title: 'About Pizza Inn',
        subtitle: 'Our Story of Authentic Italian Cuisine',
        history: {
          title: 'Our History',
          content: 'Since 1995, Pizza Inn has been serving authentic Italian pizza in the heart of Baku. What started as a small family dream has grown into one of the city\'s most beloved dining destinations.'
        },
        mission: {
          title: 'Our Mission',
          content: 'We are committed to bringing you the most authentic Italian pizza experience using traditional recipes, premium ingredients, and time-honored cooking techniques passed down through generations.'
        },
        values: {
          title: 'Our Values',
          quality: 'Quality First',
          qualityDesc: 'Only the finest ingredients make it to our kitchen',
          tradition: 'Traditional Methods',
          traditionDesc: 'Authentic Italian recipes and cooking techniques',
          service: 'Exceptional Service',
          serviceDesc: 'Every customer is part of our extended family',
          community: 'Community Focus',
          communityDesc: 'Proud to serve the Baku community for nearly three decades'
        }
      },
      
      // Contact page
      contact: {
        title: 'Contact Us',
        subtitle: 'Get in Touch',
        getInTouch: 'We\'d love to hear from you',
        form: {
          title: 'Send us a Message',
          namePlaceholder: 'Your Name',
          emailPlaceholder: 'Your Email',
          subjectPlaceholder: 'Subject',
          messagePlaceholder: 'Your Message',
          send: 'Send Message',
          sending: 'Sending...',
          success: 'Thank you! Your message has been sent. We will get back to you soon.',
          error: 'Your message could not be sent. Please try again.',
          networkError: 'Network error. Please check your connection and try again.'
        },
        info: {
          title: 'Visit Us',
          address: 'Address',
          addressFull: 'Fountain Square 8, Baku, Azerbaijan',
          phone: 'Phone',
          email: 'Email',
          hours: 'Opening Hours',
          hoursWeekdays: 'Monday - Sunday',
          hoursTime: '8:00 AM - 3:00 AM'
        }
      },
      
      // Reservations page
      reservations: {
        title: 'Make a Reservation',
        subtitle: 'Book Your Table',
        description: 'Reserve your table for an unforgettable dining experience at Pizza Inn Baku',
        form: {
          title: 'Reservation Details',
          guestsLabel: 'Number of Guests',
          dateLabel: 'Preferred Date',
          timeLabel: 'Preferred Time',
          specialRequestsLabel: 'Special Requests',
          specialRequestsPlaceholder: 'Any special dietary requirements or requests?',
          reserve: 'Make Reservation',
          sending: 'Booking...',
          success: 'Thank you! Your reservation request has been received. We will confirm it shortly.',
          error: 'Your reservation could not be submitted. Please try again.',
          networkError: 'Network error. Please check your connection and try again.'
        },
        info: {
          title: 'Reservation Information',
          note1: 'Reservations are recommended, especially for dinner service',
          note2: 'Large groups (8+ people) require advance booking',
          note3: 'We accept reservations up to 30 days in advance',
          contact: 'For immediate assistance, call us at'
        }
      },

      // Gallery page
      gallery: {
        title: 'Gallery',
        subtitle: 'Beautiful photos of our dishes and restaurant',
        loading: 'Loading gallery...',
        empty: 'No photos yet. Check back soon!',
        loadError: 'The gallery could not be loaded. Please try again.',
        networkError: 'Network error. Please check your connection and try again.',
        retry: 'Try Again'
      },
      
      // Menu
      menu: {
        title: 'Our Menu',
        welcome: {
          eyebrow: 'Welcome',
          body1: 'Since 1995, Pizza Inn has worked to give our guests enjoyable moments. Whether you prefer the classic flavours you love or want to discover something new, we present our rich menu, prepared specially for you.',
          body2: 'In our menu we use the freshest, highest-quality ingredients, bringing together both the rich heritage of Azerbaijani cuisine and examples of international cooking.',
          body3: 'We hope your meal makes your visit more comfortable and delicious.',
          closing: 'Bon appétit!'
        },
        eyebrow: 'Fountain Square 8 · Baku',
        gardenTitle: 'The Garden',
        gardenEyebrow: 'Winter Park · Baku',
        subtitle: 'Discover our delicious selection of authentic pizzas and more',
        categories: 'Categories',
        search: 'Search menu items...',
        addToCart: 'Add to Cart',
        viewDetails: 'View Details',
        price: 'Price',
        ingredients: 'Ingredients',
        all: 'All Items',
        noResults: 'No items found matching your search',
        noItems: 'No items in this category',
        featured: 'Featured',
        unavailable: 'Unavailable',
        totalItems: 'Total Items',
        birthdayMenu: '🎉 Birthday Menu 🎉',
        newYearMenu: '✨ New Year Menu ✨'
      },

      // Venue page captions
      // Brand menu pages (BrandMenu) and the ALL menu's own strings
      brands: {
        qisparki: {
          seoTitle: 'Pizza Inn Garden - Menu',
          seoDescription: 'Pizza Inn Garden restaurant menu'
        },
        sedef: {
          eyebrow: 'Sədəf · Baku',
          title: 'Sadaf National Cuisine',
          caption: 'The Sədəf table: dishes prepared fresh, every day.',
          seoTitle: 'Sadaf National Cuisine - Menu',
          seoDescription: 'Sadaf National Cuisine (Sədəf Milli Mətbəx) restaurant menu, Baku',
          serviceCharge: 'Service charge 10%',
          photos: 'Photos',
          hasPhoto: 'Has photo'
        }
      },
      allMenu: {
        loadError: 'Could not load the menu. Please try again.'
      },
      qisparki: {
        caption: 'Native flavours, a generous table. The most loved tastes of Azerbaijani cuisine, in the Garden.'
      },
      mokaland: {
        caption: 'The sweetest taste of fun is at Moka Land! Play for the kids, delight for the whole family.'
      },

      // Birthday Menu
      birthdayMenu: {
        backToMenu: 'Back to Kids Menu',
        title: 'Birthday Celebration Menu',
        subtitle: 'Make your child\'s special day magical!',
        description: 'Celebrate your little one\'s birthday with our special menu designed for unforgettable parties! From delicious treats to fun activities, we have everything to make their day perfect.',
        viewFullscreen: 'View Fullscreen',
        download: 'Download Menu',
        bookParty: 'Book a Party',
        loading: 'Loading birthday menu...',
        errorTitle: 'Oops! Menu not available',
        errorMessage: 'The birthday menu couldn\'t be loaded. Please try downloading it instead!',
        retry: 'Try Again',
        pdfTitle: 'Birthday Menu PDF',
        featuresTitle: 'Party Features',
        feature1: 'Kid-friendly menu options',
        feature2: 'Custom birthday cakes',
        feature3: 'Party decorations included',
        feature4: 'Special birthday surprises',
        contactTitle: 'Book Your Party',
        contactDescription: 'Ready to plan an amazing birthday party? Contact us to make reservations and customize your celebration!',
        contact: 'Contact Us',
        reserve: 'Make Reservation',
        stat1: 'Happy Birthday Parties',
        stat2: 'Happy Kids',
        stat3: 'Parent Reviews'
      },
      
      // New Year Menu
      newYearMenu: {
        backToMenu: 'Back to Kids Menu',
        title: 'New Year Celebration Menu',
        subtitle: 'Welcome the New Year with unforgettable entertainment packages!',
        description: 'Celebrate the New Year with our special entertainment packages! Enjoy music, fun games, delicious food, and create magical memories for children and families.',
        viewFullscreen: 'View Fullscreen',
        download: 'Download Menu',
        bookEvent: 'Book New Year Event',
        loading: 'Loading New Year menu...',
        errorTitle: 'Oops! Menu not available',
        errorMessage: 'The New Year menu couldn\'t be loaded. Please try downloading it instead!',
        retry: 'Try Again',
        imageAlt: 'Moka Land New Year Menu - Festive celebration packages for children',
        featuresTitle: 'Celebration Features',
        feature1: 'Entertainment zone with games',
        feature2: 'Music and fun activities',
        feature3: 'Delicious food packages',
        feature4: 'Special New Year surprises',
        feature5: 'Festive atmosphere and decorations',
        contactTitle: 'Book Your New Year Celebration',
        contactDescription: 'Ready to celebrate the New Year in style? Contact us to reserve your entertainment package and make this New Year unforgettable!',
        contact: 'Contact Us',
        reserve: 'Make Reservation',
        happyPackage: 'Perfect for smaller celebrations with entertainment zone, games, pizza, nuggets, and fruit juice.',
        magicPackage: 'Enhanced celebration with additional treats, salad, Ice Tea, and special Santa program.',
        premiumPackage: 'Ultimate New Year experience with full menu, multiple salads, beverages, and exclusive Santa program.',
        stat1: 'New Year Celebrations',
        stat2: 'Happy Children',
        stat3: 'Family Reviews'
      },
      
      // Cart
      cart: {
        title: 'Shopping Cart',
        empty: 'Your cart is empty',
        subtotal: 'Subtotal',
        serviceFee: 'Service Fee',
        delivery: 'Delivery Fee',
        total: 'Total',
        checkout: 'Checkout',
        remove: 'Remove',
        quantity: 'Quantity',
        continueShopping: 'Continue Shopping',
        proceedToCheckout: 'Proceed to Checkout'
      },
      
      // Orders
      order: {
        title: 'Order Online',
        subtitle: 'Choose your delivery platform',
        description: 'Order Pizza Inn through your favorite delivery app',
        status: 'Order Status',
        track: 'Track Order',
        success: 'Order Successful',
        failed: 'Order Failed',
        pending: 'Pending',
        confirmed: 'Confirmed',
        preparing: 'Preparing',
        ready: 'Ready',
        delivered: 'Delivered',
        delivery: {
          wolt: {
            name: 'Wolt',
            action: 'Order with Wolt',
            description: 'Fast and reliable delivery',
            time: '25-40 min'
          },
          boltFood: {
            name: 'Bolt Food',
            action: 'Order with Bolt Food',
            description: 'Quick delivery to your door',
            time: '20-35 min'
          },
          choosePlatform: 'Choose Your Delivery Platform',
          availableIn: 'Available throughout Baku',
          estimatedTime: 'Estimated delivery time',
          openApp: 'Open App',
          orWeb: 'or visit website'
        }
      },
      
      // Location
      location: {
        title: 'Find Pizza Inn',
        subtitle: 'Get directions to Pizza Inn Baku at Fountain Square',
        description: 'Choose your preferred navigation app to get turn-by-turn directions',
        address: 'Fountain Square 8, Baku, Azerbaijan',
        navigation: {
          googleMaps: {
            name: 'Google Maps',
            action: 'Open Google Maps',
            description: 'Get accurate driving directions with real-time traffic updates and street view.'
          },
          waze: {
            name: 'Waze',
            action: 'Open Waze',
            description: 'Community-driven navigation with fastest routes and traffic alerts.'
          },
          realTimeTraffic: 'Real-time traffic & directions',
          accurate: 'Most accurate mapping',
          fastestRoute: 'Fastest route optimization',
          communityAlerts: 'Community traffic alerts',
          openWeb: 'Opens in web browser',
          openApp: 'Opens navigation app',
          orWeb: 'or web'
        },
        restaurantInfo: {
          title: 'Pizza Inn Baku',
          address: 'Fountain Square 8, Baku',
          hours: 'Open daily: 8:00 AM - 3:00 AM'
        }
      },
      
      // Common
      common: {
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        save: 'Save',
        cancel: 'Cancel',
        edit: 'Edit',
        delete: 'Delete',
        confirm: 'Confirm',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        email: 'Email',
        password: 'Password',
        name: 'Name',
        phone: 'Phone',
        address: 'Address',
        submit: 'Submit',
        required: 'Required',
        optional: 'Optional'
      },
      
      // Forms
      forms: {
        firstName: 'First Name',
        lastName: 'Last Name',
        fullName: 'Full Name',
        emailAddress: 'Email Address',
        phoneNumber: 'Phone Number',
        message: 'Message',
        subject: 'Subject',
        date: 'Date',
        time: 'Time',
        guests: 'Number of Guests',
        specialRequests: 'Special Requests'
      },
      
      // Admin
      admin: {
        dashboard: 'Dashboard',
        orders: 'Orders',
        menu: 'Menu Management',
        reservations: 'Reservations',
        gallery: 'Gallery',
        contacts: 'Contacts',
        settings: 'Settings',
        analytics: 'Analytics',
        users: 'Users',
        reports: 'Reports'
      },
      
      // Footer
      footer: {
        contact: 'Contact Info',
        hours: 'Opening Hours',
        social: 'Follow Us',
        address: 'Address',
        phone: 'Phone',
        email: 'Email',
        addressText: 'Fountain Square 8, Baku, Azerbaijan',
        quickLinks: 'Quick Links',
        aboutUs: 'About Pizza Inn',
        description: 'Authentic Italian pizza in the heart of Baku',
        tagline: 'The classics of Azerbaijani cuisine and the finest flavours of world cuisine.',
        everyday: 'Every Day'
      }
    }
  },
  
  az: {
    translation: {
      nav: {
        startOrder: 'Sifarişə başla',
        reviewOrder: 'Sifarişə bax',
        home: 'Ana Səhifə',
        menu: 'Menyu',
        about: 'Haqqımızda',
        gallery: 'Qalereya',
        contact: 'Əlaqə',
        location: 'Yer',
        reservations: 'Rezervasiya',
        order: 'Online Sifariş',
        cart: 'Səbət',
        language: 'Dil',
        profile: 'Profil',
        login: 'Giriş',
        logout: 'Çıxış',
        admin: 'Admin'
      },
      
      home: {
        hero: {
          orderNow: 'İndi Sifariş Et',
          viewMenu: 'Menyuya Bax',
          viewMokalandMenu: 'Moka Land Menyusuna Bax',
          eyebrow: 'Fəvvarələr Meydanı 8 · Bakı',
          titleLine1: 'Fəvvarələr Meydanı,',
          titleLine2: '1995-ci ildən.',
          sub: 'Böyük şəhərin kiçik hekayəsi.',
          seeMenu: 'Menyuya bax →',
          hours: 'İndi açıqdır · 08:00–03:00',
          location: 'Fəvvarələr Meydanı 8, Bakı'
        },
        highlights: {
          awardWinning: 'Mükafat Qazanan',
          awardDesc: '692+ məmnun müştəridən 4.0★ reytinq',
          freshDaily: 'Hər Gün Təzə',
          freshDailyDesc: 'Premium inqrediyentlərlə hər gün təzə hazırlanır',
          fastDelivery: 'Sürətli Çatdırılma',
          fastDeliveryDesc: 'Bakının məhəllələrinə sürətli çatdırılma'
        },
        businessInfo: {
          hours: 'İş Saatları:',
          hoursText: 'Hər gün 8:00 - 3:00',
          phone: 'Telefon:',
          phoneNumber: '*9777'
        },
        cta: {
          title: 'Orijinal Pizza İstəyirsiniz?',
          subtitle: 'Bakı üzrə çatdırılma üçün indi sifariş verin və ya Fəvvarələr Meydanında bizə baş çəkin. Gecə 3:00-a qədər açıq!',
          order: 'Online Sifariş',
          menu: 'Menyuya Bax'
        },
        heritage: {
          since: 'Bəri',
          quote: 'Milli mətbəximizin zəngin irsi, dünya mətbəxinin seçilən ləzzətləri. İllərin təcrübəsi və incə zövqlə hər süfrədə davam edən hekayə.',
        },
        stats: {
          years: 'Bakıda İllər',
          hours: 'Hər Gün Açıq',
          locationValue: 'Fəvvarələr Meydanı',
          location: 'Bakı, Azərbaycan',
        },
        instagram: {
          label: 'Bizi İzləyin',
          title: '@pizzainnbaku',
          body: 'Gündəlik təkliflər, mövsümi yeməklər və mətbəxdən anlar üçün bizi izləyin.',
          follow: 'Instagram-da İzləyin',
        },
        delivery: {
          orderVia: 'Çatdırılma',
          orderDirect: 'Birbaşa Sifariş',
        },
        chapters: {
          eyebrow: 'Pizza Inn',
          title: 'Hərəkətdə',
          contentsLabel: 'Mündəricat',
          menuCta: 'Bütün menyuya bax',
          pizza: {
            name: 'Pizza',
            eyebrow: 'Evimiz',
            subject: 'Sıfırdan hazırlanır',
            alt: 'Pizza Inn-də əl ilə hazırlanan pizza',
            gloss: 'Soba, un və kürək.',
          },
          breakfast: {
            name: 'Səhər yeməyi',
            eyebrow: 'Səhər',
            subject: 'Səhər yeməyi süfrədə',
            alt: 'Pizza Inn-də səhər yeməyi verilir',
            gloss: 'Yumurta, çörək və günün ilk süfrəsi.',
          },
          coffee: {
            name: 'Qəhvə',
            eyebrow: 'Bar',
            subject: 'Qəhvə və kruassan',
            alt: 'Pizza Inn-də qəhvə və kruassan',
            gloss: 'Kiçik fincanlar, uzun günorta saatları.',
          },
          grill: {
            name: 'Manqal',
            eyebrow: 'Axşam',
            subject: 'Manqaldan',
            alt: 'Pizza Inn-də bişirilən steyk',
            gloss: 'Köz, tandır və paylaşmaq üçün tikələr.',
          },
          garden: {
            name: 'Garden',
            eyebrow: 'Toran',
            subject: 'Garden',
            alt: 'Pizza Inn Garden, həm içəridə, həm çöldə',
            gloss: 'Damı olmayan otaq.',
            body: 'Doğma dadlar, zəngin süfrə. Azərbaycan mətbəxinin ən sevilən ləzzətləri Garden-də.',
            cta: 'Garden-ə baş çək',
          },
          mokaland: {
            name: 'Moka Land',
            eyebrow: 'Ailə üçün',
            subject: 'Əyləncənin ən şirin dadı',
            alt: 'Moka Land-də oynayan və yemək yeyən uşaqlar',
            gloss: 'İp, halqalar və hamı üçün bir süfrə.',
            body: 'Əyləncənin ən şirin dadı Moka Land-də! Uşaqlara əyləncə, bütün ailəyə ləzzət.',
            cta: 'Moka Land-ə baş çək',
          },
        },
      },

      about: {
        title: 'Pizza Inn Haqqında',
        subtitle: 'Orijinal İtalyan Mətbəxi Hekayəmiz',
        history: {
          title: 'Bizim Tarixçəmiz',
          content: '1995-ci ildən Pizza Inn Bakının mərkəzində orijinal İtalyan pizzası təqdim edir. Kiçik ailə arzusu olaraq başlayan bu məkan şəhərin ən sevimli yemək məkanlarından birinə çevrildi.'
        },
        mission: {
          title: 'Bizim Missiyamız',
          content: 'Biz sizə ənənəvi reseptlər, premium inqrediyentlər və nəsillər boyu ötürülən vaxtla sınaqdan çıxmış bişirmə texnikaları istifadə edərək ən orijinal İtalyan pizza təcrübəsini təqdim etməyə sadiqik.'
        },
        values: {
          title: 'Bizim Dəyərlərimiz',
          quality: 'Keyfiyyət Əvvəldə',
          qualityDesc: 'Yalnız ən yaxşı inqrediyentlər mətbəximizə daxil olur',
          tradition: 'Ənənəvi Üsullar',
          traditionDesc: 'Orijinal İtalyan reseptləri və bişirmə texnikaları',
          service: 'Müstəsna Xidmət',
          serviceDesc: 'Hər müştəri bizim böyük ailəmizin bir hissəsidir',
          community: 'Cəmiyyət Diqqəti',
          communityDesc: 'Otuz ilə yaxın Bakı cəmiyyətinə xidmət etməkdən qürur duyuruq'
        }
      },
      
      contact: {
        title: 'Bizimlə Əlaqə',
        subtitle: 'Əlaqə Saxlayın',
        getInTouch: 'Sizdən eşitməkdən məmnun olarıq',
        form: {
          title: 'Bizə Mesaj Göndərin',
          namePlaceholder: 'Adınız',
          emailPlaceholder: 'E-poçtunuz',
          subjectPlaceholder: 'Mövzu',
          messagePlaceholder: 'Mesajınız',
          send: 'Mesaj Göndər',
          sending: 'Göndərilir...',
          success: 'Təşəkkür edirik! Mesajınız göndərildi. Tezliklə sizinlə əlaqə saxlayacağıq.',
          error: 'Mesajınız göndərilə bilmədi. Zəhmət olmasa yenidən cəhd edin.',
          networkError: 'Şəbəkə xətası. Bağlantınızı yoxlayıb yenidən cəhd edin.'
        },
        info: {
          title: 'Bizə Baş Çəkin',
          address: 'Ünvan',
          addressFull: 'Fəvvarələr Meydanı 8, Bakı, Azərbaycan',
          phone: 'Telefon',
          email: 'E-poçt',
          hours: 'İş Saatları',
          hoursWeekdays: 'Bazar ertəsi - Bazar',
          hoursTime: '8:00 - 3:00'
        }
      },
      
      reservations: {
        title: 'Rezervasiya Et',
        subtitle: 'Masanızı Rezerv Edin',
        description: 'Pizza Inn Bakıda unudulmaz yemək təcrübəsi üçün masanızı rezerv edin',
        form: {
          title: 'Rezervasiya Təfərrüatları',
          guestsLabel: 'Qonaq Sayı',
          dateLabel: 'Üstünlük Verilən Tarix',
          timeLabel: 'Üstünlük Verilən Vaxt',
          specialRequestsLabel: 'Xüsusi İstəklər',
          specialRequestsPlaceholder: 'Xüsusi pəhriz tələbləri və ya istəkləriniz varmı?',
          reserve: 'Rezervasiya Et',
          sending: 'Rezervasiya edilir...',
          success: 'Təşəkkür edirik! Rezervasiya sorğunuz qəbul edildi. Tezliklə təsdiqləyəcəyik.',
          error: 'Rezervasiyanız göndərilə bilmədi. Zəhmət olmasa yenidən cəhd edin.',
          networkError: 'Şəbəkə xətası. Bağlantınızı yoxlayıb yenidən cəhd edin.'
        },
        info: {
          title: 'Rezervasiya Məlumatları',
          note1: 'Rezervasiyalar tövsiyə olunur, xüsusən də şam xidməti üçün',
          note2: 'Böyük qruplar (8+ nəfər) əvvəlcədən rezervasiya tələb edir',
          note3: 'Biz 30 günə qədər əvvəlcədən rezervasiya qəbul edirik',
          contact: 'Dərhal kömək üçün bizə zəng edin'
        }
      },

      gallery: {
        title: 'Qalereya',
        subtitle: 'Yeməklərimizin və restoranımızın gözəl fotoşəkilləri',
        loading: 'Qalereya yüklənir...',
        empty: 'Hələ foto yoxdur. Tezliklə yenidən baxın!',
        loadError: 'Qalereya yüklənə bilmədi. Zəhmət olmasa yenidən cəhd edin.',
        networkError: 'Şəbəkə xətası. Bağlantınızı yoxlayıb yenidən cəhd edin.',
        retry: 'Yenidən Cəhd Et'
      },
      
      menu: {
        title: 'Bizim Menyu',
        welcome: {
          eyebrow: 'Xoş gəlmisiniz',
          body1: '1995-ci ildən etibarən Pizza Inn olaraq qonaqlarımıza zövq dolu anlar yaşatmaq üçün çalışırıq. İstər sevdiyiniz klassik dadlara üstünlük verin, istərsə də yeni dadlar kəşf etmək istəsəniz, sizin üçün xüsusi olaraq hazırlanmış zəngin menyumuzu təqdim edirik.',
          body2: 'Menyumuzda ən təzə və keyfiyyətli məhsullardan istifadə edərək, həm Azərbaycan mətbəxinin zəngin irsini, həm də beynəlxalq kulinariya nümunələrini bir araya gətiririk.',
          body3: 'Ümid edirik ki, yeməyiniz səfərinizi daha rahat və ləzzətli edəcək.',
          closing: 'Nuş olsun!'
        },
        eyebrow: 'Fəvvarələr Meydanı 8 · Bakı',
        gardenTitle: 'Qış Parkı',
        gardenEyebrow: 'Qış Parkı · Bakı',
        subtitle: 'Orijinal pizzalarımızın və daha çox dadlı seçimlərimizin kəşf edin',
        categories: 'Kateqoriyalar',
        search: 'Menyu məhsullarını axtar...',
        addToCart: 'Səbətə Əlavə Et',
        viewDetails: 'Təfərrüatlara Bax',
        price: 'Qiymət',
        ingredients: 'İnqrediyentlər',
        all: 'Bütün Məhsullar',
        noResults: 'Axtarışınıza uyğun məhsul tapılmadı',
        noItems: 'Bu kateqoriyada məhsul yoxdur',
        featured: 'Seçilmiş',
        unavailable: 'Mövcud deyil',
        totalItems: 'Ümumi Məhsullar',
        birthdayMenu: '🎉 Ad Günü Menyusu 🎉',
        newYearMenu: '✨ Yeni İl Menyusu ✨'
      },

      // Venue page captions
      // Brand menu pages (BrandMenu) and the ALL menu's own strings
      brands: {
        qisparki: {
          seoTitle: 'Pizza Inn Qış Parkı - Menyu',
          seoDescription: 'Pizza Inn Qış Parkı restoranının menyusu'
        },
        sedef: {
          eyebrow: 'Sədəf · Bakı',
          title: 'Sədəf Milli Mətbəx',
          caption: 'Sədəf süfrəsi: hər gün təzə hazırlanan yeməklər.',
          seoTitle: 'Sədəf Milli Mətbəx | Menyu',
          seoDescription: 'Sədəf Milli Mətbəx restoranının menyusu, Bakı',
          serviceCharge: 'Xidmət haqqı 10 %',
          photos: 'Şəkillər',
          hasPhoto: 'Şəkli var'
        }
      },
      allMenu: {
        loadError: 'Menyu yüklənmədi. Zəhmət olmasa yenidən cəhd edin.'
      },
      qisparki: {
        caption: 'Doğma dadlar, zəngin süfrə. Azərbaycan mətbəxinin ən sevilən ləzzətləri Garden-də.'
      },
      mokaland: {
        caption: 'Əyləncənin ən şirin dadı Moka Land-də! Uşaqlara əyləncə, bütün ailəyə ləzzət.'
      },

      // Birthday Menu
      birthdayMenu: {
        backToMenu: 'Uşaq Menyusuna Qayıt',
        title: 'Ad Günü Qeyd Etmə Menyusu',
        subtitle: 'Uşağınızın xüsusi gününü sehrli edin!',
        description: 'Kiçik balalarınızın ad gününü unudulmaz ziyafətlər üçün xüsusi olaraq hazırlanmış menyumuzla qeyd edin! Dadlı təamlardan əyləncəli fəaliyyətlərə qədər, onların gününü mükəmməl etmək üçün hər şeyə sahibik.',
        viewFullscreen: 'Tam Ekranda Bax',
        download: 'Menyunu Yüklə',
        bookParty: 'Şənlik Rezerv Et',
        loading: 'Ad günü menyusu yüklənir...',
        errorTitle: 'Vay! Menyu mövcud deyil',
        errorMessage: 'Ad günü menyusu yüklənə bilmədi. Zəhmət olmasa əvəzinə onu yükləməyi sınayın!',
        retry: 'Yenidən Cəhd Et',
        pdfTitle: 'Ad Günü Menyusu PDF',
        featuresTitle: 'Şənlik Xüsusiyyətləri',
        feature1: 'Uşaq dostu menyu seçimləri',
        feature2: 'Xüsusi ad günü tortları',
        feature3: 'Şənlik bəzəkləri daxildir',
        feature4: 'Xüsusi ad günü sürprizləri',
        contactTitle: 'Şənliyinizi Rezerv Edin',
        contactDescription: 'Heyrətamiz ad günü şənliyi planlaşdırmağa hazırsınız? Rezervasiyalar etmək və qeyd etmənizi fərdiləşdirmək üçün bizimlə əlaqə saxlayın!',
        contact: 'Bizimlə Əlaqə Saxlayın',
        reserve: 'Rezervasiya Et',
        stat1: 'Xoşbəxt Ad Günü Şənlikləri',
        stat2: 'Xoşbəxt Uşaqlar',
        stat3: 'Valideyn Rəyləri'
      },
      
      // New Year Menu  
      newYearMenu: {
        backToMenu: 'Uşaq Menyusuna Qayıt',
        title: 'Yeni İl Qeyd Etmə Menyusu',
        subtitle: 'Yeni İli unudulmaz əyləncə paketləri ilə qarşılayın!',
        description: 'Yeni İli xüsusi əyləncə paketlərimizla qeyd edin! Musiqi, əyləncəli oyunlar, dadlı yeməklər və uşaqlar və ailələr üçün sehrli xatirələr yaradın.',
        viewFullscreen: 'Tam Ekranda Bax',
        download: 'Menyunu Yüklə',
        bookEvent: 'Yeni İl Tədbirini Rezerv Et',
        loading: 'Yeni İl menyusu yüklənir...',
        errorTitle: 'Vay! Menyu mövcud deyil',
        errorMessage: 'Yeni İl menyusu yüklənə bilmədi. Zəhmət olmasa əvəzinə onu yükləməyi sınayın!',
        retry: 'Yenidən Cəhd Et',
        imageAlt: 'Moka Land Yeni İl Menyusu - Uşaqlar üçün şənlik qeyd etmə paketləri',
        featuresTitle: 'Qeyd Etmə Xüsusiyyətləri',
        feature1: 'Oyun zonası ilə əyləncə',
        feature2: 'Musiqi və əyləncəli fəaliyyətlər',
        feature3: 'Dadlı yemək paketləri',
        feature4: 'Xüsusi Yeni İl sürprizləri',
        feature5: 'Şənlik atmosferi və bəzəkləri',
        contactTitle: 'Yeni İl Qeyd Etmənizi Rezerv Edin',
        contactDescription: 'Yeni İli üslubla qeyd etməyə hazırsınız? Əyləncə paketinizi rezerv etmək və bu Yeni İli unudulmaz etmək üçün bizimlə əlaqə saxlayın!',
        contact: 'Bizimlə Əlaqə Saxlayın',
        reserve: 'Rezervasiya Et',
        happyPackage: 'Kiçik qeyd etmələr üçün mükəmməl - əyləncə zonası, oyunlar, pizza, nugget və meyvə şirəsi.',
        magicPackage: 'Təkmilləşdirilmiş qeyd etmə - əlavə dadlı şeylər, salat, İce Tea və xüsusi Şaxta Baba proqramı.',
        premiumPackage: 'Ən yüksək Yeni İl təcrübəsi - tam menyu, çox sayda salat, içkilər və ekskluziv Şaxta Baba proqramı.',
        stat1: 'Yeni İl Qeyd Etmələri',
        stat2: 'Xoşbəxt Uşaqlar',
        stat3: 'Ailə Rəyləri'
      },
      
      cart: {
        title: 'Alış-Veriş Səbəti',
        empty: 'Səbətiniz boşdur',
        subtotal: 'Ara Cəm',
        serviceFee: 'Xidmət haqqı',
        delivery: 'Çatdırılma Haqqı',
        total: 'Ümumi',
        checkout: 'Ödəniş',
        remove: 'Sil',
        quantity: 'Miqdar',
        continueShopping: 'Alış-verişə Davam Et',
        proceedToCheckout: 'Ödənişə Keç'
      },
      
      order: {
        title: 'Online Sifariş',
        subtitle: 'Çatdırılma platformanızı seçin',
        description: 'Sevimli çatdırılma tətbiqi ilə Pizza Inn sifariş edin',
        status: 'Sifariş Statusu',
        track: 'Sifarişi İzlə',
        success: 'Sifariş Uğurlu',
        failed: 'Sifariş Uğursuz',
        pending: 'Gözləyir',
        confirmed: 'Təsdiqlənib',
        preparing: 'Hazırlanır',
        ready: 'Hazırdır',
        delivered: 'Çatdırılıb',
        delivery: {
          wolt: {
            name: 'Wolt',
            action: 'Wolt ilə Sifariş Et',
            description: 'Sürətli və etibarlı çatdırılma',
            time: '25-40 dəq'
          },
          boltFood: {
            name: 'Bolt Food',
            action: 'Bolt Food ilə Sifariş Et',
            description: 'Qapınıza sürətli çatdırılma',
            time: '20-35 dəq'
          },
          choosePlatform: 'Çatdırılma Platformanızı Seçin',
          availableIn: 'Bütün Bakı üzrə mövcud',
          estimatedTime: 'Təxmini çatdırılma vaxtı',
          openApp: 'Tətbiqi Aç',
          orWeb: 'və ya saytı ziyarət edin'
        }
      },
      
      common: {
        loading: 'Yüklənir...',
        error: 'Xəta',
        success: 'Uğur',
        save: 'Saxla',
        cancel: 'Ləğv Et',
        edit: 'Redaktə Et',
        delete: 'Sil',
        confirm: 'Təsdiq Et',
        close: 'Bağla',
        back: 'Geri',
        next: 'Növbəti',
        previous: 'Əvvəlki',
        email: 'E-poçt',
        password: 'Parol',
        name: 'Ad',
        phone: 'Telefon',
        address: 'Ünvan',
        submit: 'Göndər',
        required: 'Məcburi',
        optional: 'İstəyə bağlı'
      },
      
      forms: {
        firstName: 'Ad',
        lastName: 'Soyad',
        fullName: 'Tam Ad',
        emailAddress: 'E-poçt Ünvanı',
        phoneNumber: 'Telefon Nömrəsi',
        message: 'Mesaj',
        subject: 'Mövzu',
        date: 'Tarix',
        time: 'Vaxt',
        guests: 'Qonaq Sayı',
        specialRequests: 'Xüsusi İstəklər'
      },
      
      admin: {
        dashboard: 'İdarə Paneli',
        orders: 'Sifarişlər',
        menu: 'Menyu İdarəsi',
        reservations: 'Rezervasiyalar',
        gallery: 'Qalereya',
        contacts: 'Əlaqələr',
        settings: 'Parametrlər',
        analytics: 'Analitika',
        users: 'İstifadəçilər',
        reports: 'Hesabatlar'
      },
      
      footer: {
        contact: 'Əlaqə Məlumatları',
        hours: 'İş Saatları',
        social: 'Bizi İzləyin',
        address: 'Ünvan',
        phone: 'Telefon',
        email: 'E-poçt',
        addressText: 'Fəvvarələr Meydanı 8, Bakı, Azərbaycan',
        quickLinks: 'Sürətli Keçidlər',
        aboutUs: 'Pizza Inn Haqqında',
        description: 'Bakının mərkəzində orijinal İtalyan pizzası',
        tagline: 'Azərbaycan mətbəxinin klassikləri və dünya mətbəxinin seçmə ləzzətləri.',
        everyday: 'Hər Gün'
      }
    }
  },
  
  ru: {
    translation: {
      nav: {
        startOrder: 'Начать заказ',
        reviewOrder: 'Проверить заказ',
        home: 'Главная',
        menu: 'Меню',
        about: 'О нас',
        gallery: 'Галерея',
        contact: 'Контакты',
        location: 'Локация',
        reservations: 'Резервация',
        order: 'Заказать онлайн',
        cart: 'Корзина',
        language: 'Язык',
        profile: 'Профиль',
        login: 'Войти',
        logout: 'Выйти',
        admin: 'Админ'
      },
      
      home: {
        hero: {
          orderNow: 'Заказать сейчас',
          viewMenu: 'Посмотреть меню',
          viewMokalandMenu: 'Посмотреть меню Moka Land',
          eyebrow: 'Площадь Фонтанов 8 · Баку',
          titleLine1: 'Площадь Фонтанов,',
          titleLine2: 'с 1995 года.',
          sub: 'Маленькая история большого города.',
          seeMenu: 'Смотреть меню →',
          hours: 'Открыто · 08:00–03:00',
          location: 'Площадь Фонтанов 8, Баку'
        },
        highlights: {
          awardWinning: 'Отмеченный наградами',
          awardDesc: 'Рейтинг 4.0★ от 692+ довольных клиентов',
          freshDaily: 'Свежее ежедневно',
          freshDailyDesc: 'Готовится свежим каждый день из премиальных ингредиентов',
          fastDelivery: 'Быстрая доставка',
          fastDeliveryDesc: 'Быстрая доставка по районам Баку'
        },
        businessInfo: {
          hours: 'Часы работы:',
          hoursText: 'Ежедневно 8:00 - 3:00',
          phone: 'Телефон:',
          phoneNumber: '*9777'
        },
        cta: {
          title: 'Хотите настоящую пиццу?',
          subtitle: 'Закажите доставку по Баку или посетите нас на Площади Фонтанов. Работаем до 3:00 утра!',
          order: 'Заказать онлайн',
          menu: 'Посмотреть меню'
        },
        heritage: {
          since: 'С',
          quote: 'Богатое наследие нашей национальной кухни и избранные вкусы мировой кухни. История, которая продолжается за каждым столом — с годами опыта и тонким вкусом.',
        },
        stats: {
          years: 'Лет в Баку',
          hours: 'Открыто Ежедневно',
          locationValue: 'Площадь Фонтанов',
          location: 'Баку, Азербайджан',
        },
        instagram: {
          label: 'Подписывайтесь',
          title: '@pizzainnbaku',
          body: 'Подписывайтесь, чтобы видеть ежедневные предложения и моменты с кухни.',
          follow: 'Подписаться в Instagram',
        },
        delivery: {
          orderVia: 'Доставка через',
          orderDirect: 'Заказать Напрямую',
        },
        chapters: {
          eyebrow: 'Pizza Inn',
          title: 'В движении',
          contentsLabel: 'Содержание',
          menuCta: 'Смотреть всё меню',
          pizza: {
            name: 'Пицца',
            eyebrow: 'Наш дом',
            subject: 'С нуля',
            alt: 'Пиццу готовят вручную в Pizza Inn',
            gloss: 'Печь, мука и лопата.',
          },
          breakfast: {
            name: 'Завтрак',
            eyebrow: 'Утро',
            subject: 'Завтрак подан',
            alt: 'Завтрак подают в Pizza Inn',
            gloss: 'Яйца, хлеб и первый стол дня.',
          },
          coffee: {
            name: 'Кофе',
            eyebrow: 'Бар',
            subject: 'Кофе и круассан',
            alt: 'Кофе и круассан в Pizza Inn',
            gloss: 'Маленькие чашки и долгие дни.',
          },
          grill: {
            name: 'Гриль',
            eyebrow: 'Вечер',
            subject: 'С гриля',
            alt: 'Стейк готовят в Pizza Inn',
            gloss: 'Угли, тандыр и мясо на общий стол.',
          },
          garden: {
            name: 'Зимний сад',
            eyebrow: 'Сумерки',
            subject: 'Зимний сад',
            alt: 'Зимний сад Pizza Inn, внутри и снаружи',
            gloss: 'Комната без крыши.',
            body: 'Родные вкусы, щедрый стол. Самые любимые блюда азербайджанской кухни — в Garden.',
            cta: 'Посетите Зимний сад',
          },
          mokaland: {
            name: 'Moka Land',
            eyebrow: 'Для семьи',
            subject: 'Самый сладкий вкус веселья',
            alt: 'Дети играют и едят в Moka Land',
            gloss: 'Канат, обручи и стол для всех.',
            body: 'Самый сладкий вкус веселья — в Moka Land! Развлечение для детей, удовольствие для всей семьи.',
            cta: 'Посетите Moka Land',
          },
        },
      },

      about: {
        title: 'О Pizza Inn',
        subtitle: 'Наша история подлинной итальянской кухни',
        history: {
          title: 'Наша история',
          content: 'С 1995 года Pizza Inn подает настоящую итальянскую пиццу в самом сердце Баку. То, что началось как маленькая семейная мечта, выросло в одно из самых любимых мест для ужина в городе.'
        },
        mission: {
          title: 'Наша миссия',
          content: 'Мы стремимся принести вам самый аутентичный опыт итальянской пиццы, используя традиционные рецепты, премиальные ингредиенты и проверенные временем техники приготовления, передаваемые из поколения в поколение.'
        },
        values: {
          title: 'Наши ценности',
          quality: 'Качество прежде всего',
          qualityDesc: 'Только лучшие ингредиенты попадают в нашу кухню',
          tradition: 'Традиционные методы',
          traditionDesc: 'Аутентичные итальянские рецепты и техники приготовления',
          service: 'Исключительное обслуживание',
          serviceDesc: 'Каждый клиент - часть нашей большой семьи',
          community: 'Фокус на сообществе',
          communityDesc: 'Гордимся тем, что обслуживаем сообщество Баку почти три десятилетия'
        }
      },
      
      contact: {
        title: 'Свяжитесь с нами',
        subtitle: 'Оставайтесь на связи',
        getInTouch: 'Мы будем рады услышать от вас',
        form: {
          title: 'Отправьте нам сообщение',
          namePlaceholder: 'Ваше имя',
          emailPlaceholder: 'Ваш email',
          subjectPlaceholder: 'Тема',
          messagePlaceholder: 'Ваше сообщение',
          send: 'Отправить сообщение',
          sending: 'Отправка...',
          success: 'Спасибо! Ваше сообщение отправлено. Мы скоро свяжемся с вами.',
          error: 'Не удалось отправить сообщение. Пожалуйста, попробуйте снова.',
          networkError: 'Ошибка сети. Проверьте подключение и попробуйте снова.'
        },
        info: {
          title: 'Посетите нас',
          address: 'Адрес',
          addressFull: 'Площадь Фонтанов 8, Баку, Азербайджан',
          phone: 'Телефон',
          email: 'Email',
          hours: 'Часы работы',
          hoursWeekdays: 'Понедельник - Воскресенье',
          hoursTime: '8:00 - 3:00'
        }
      },
      
      reservations: {
        title: 'Забронировать столик',
        subtitle: 'Забронируйте ваш стол',
        description: 'Забронируйте ваш стол для незабываемого ужина в Pizza Inn Баку',
        form: {
          title: 'Детали бронирования',
          guestsLabel: 'Количество гостей',
          dateLabel: 'Предпочтительная дата',
          timeLabel: 'Предпочтительное время',
          specialRequestsLabel: 'Особые пожелания',
          specialRequestsPlaceholder: 'Есть ли особые диетические требования или пожелания?',
          reserve: 'Забронировать',
          sending: 'Бронирование...',
          success: 'Спасибо! Ваш запрос на бронирование получен. Мы скоро подтвердим его.',
          error: 'Не удалось отправить бронирование. Пожалуйста, попробуйте снова.',
          networkError: 'Ошибка сети. Проверьте подключение и попробуйте снова.'
        },
        info: {
          title: 'Информация о бронировании',
          note1: 'Бронирование рекомендуется, особенно для ужина',
          note2: 'Большие группы (8+ человек) требуют предварительного бронирования',
          note3: 'Мы принимаем бронирования до 30 дней заранее',
          contact: 'Для немедленной помощи звоните нам по номеру'
        }
      },

      gallery: {
        title: 'Галерея',
        subtitle: 'Красивые фотографии наших блюд и ресторана',
        loading: 'Загрузка галереи...',
        empty: 'Фотографий пока нет. Заходите позже!',
        loadError: 'Не удалось загрузить галерею. Пожалуйста, попробуйте снова.',
        networkError: 'Ошибка сети. Проверьте подключение и попробуйте снова.',
        retry: 'Повторить'
      },
      
      menu: {
        title: 'Наше меню',
        eyebrow: 'Площадь Фонтанов 8 · Баку',
        gardenTitle: 'Зимний сад',
        gardenEyebrow: 'Зимний парк · Баку',
        welcome: {
          eyebrow: 'Добро пожаловать',
          body1: 'С 1995 года Pizza Inn старается подарить нашим гостям приятные моменты. Предпочитаете ли вы любимые классические вкусы или хотите открыть для себя что-то новое — мы предлагаем вам богатое меню, приготовленное специально для вас.',
          body2: 'В нашем меню мы используем самые свежие и качественные продукты, объединяя богатое наследие азербайджанской кухни и примеры международной кулинарии.',
          body3: 'Надеемся, что ваша трапеза сделает ваш визит приятнее и вкуснее.',
          closing: 'Приятного аппетита!'
        },
        subtitle: 'Откройте для себя наш восхитительный выбор настоящих пицц и многое другое',
        categories: 'Категории',
        search: 'Поиск блюд в меню...',
        addToCart: 'В корзину',
        viewDetails: 'Подробнее',
        price: 'Цена',
        ingredients: 'Ингредиенты',
        all: 'Все блюда',
        noResults: 'Не найдено блюд по вашему запросу',
        noItems: 'В этой категории нет блюд',
        featured: 'Рекомендуемые',
        unavailable: 'Недоступно',
        totalItems: 'Всего блюд'
      },

      // Venue page captions
      // Brand menu pages (BrandMenu) and the ALL menu's own strings
      brands: {
        qisparki: {
          seoTitle: 'Pizza Inn Зимний сад - Меню',
          seoDescription: 'Меню ресторана Pizza Inn Зимний сад'
        },
        sedef: {
          eyebrow: 'Sədəf · Баку',
          title: 'Садаф — национальная кухня',
          caption: 'Стол Sədəf: блюда, приготовленные свежими каждый день.',
          seoTitle: 'Садаф — национальная кухня | Меню',
          seoDescription: 'Меню ресторана Садаф — национальная кухня (Sədəf Milli Mətbəx), Баку',
          serviceCharge: 'Обслуживание 10 %',
          photos: 'Фото',
          hasPhoto: 'Есть фото'
        }
      },
      allMenu: {
        loadError: 'Не удалось загрузить меню. Пожалуйста, попробуйте снова.'
      },
      qisparki: {
        caption: 'Родные вкусы, щедрый стол. Самые любимые блюда азербайджанской кухни — в Garden.'
      },
      mokaland: {
        caption: 'Самый сладкий вкус веселья — в Moka Land! Развлечение для детей, удовольствие для всей семьи.'
      },

      cart: {
        title: 'Корзина покупок',
        empty: 'Ваша корзина пуста',
        subtotal: 'Подитог',
        serviceFee: 'Сервисный сбор',
        delivery: 'Доставка',
        total: 'Итого',
        checkout: 'Оформить заказ',
        remove: 'Удалить',
        quantity: 'Количество',
        continueShopping: 'Продолжить покупки',
        proceedToCheckout: 'Перейти к оформлению'
      },
      
      order: {
        title: 'Заказать онлайн',
        subtitle: 'Выберите платформу доставки',
        description: 'Закажите Pizza Inn через ваше любимое приложение доставки',
        status: 'Статус заказа',
        track: 'Отследить заказ',
        success: 'Заказ успешен',
        failed: 'Заказ не удался',
        pending: 'В ожидании',
        confirmed: 'Подтвержден',
        preparing: 'Готовится',
        ready: 'Готов',
        delivered: 'Доставлен',
        delivery: {
          wolt: {
            name: 'Wolt',
            action: 'Заказать с Wolt',
            description: 'Быстрая и надежная доставка',
            time: '25-40 мин'
          },
          boltFood: {
            name: 'Bolt Food',
            action: 'Заказать с Bolt Food',
            description: 'Быстрая доставка к вашей двери',
            time: '20-35 мин'
          },
          choosePlatform: 'Выберите Платформу Доставки',
          availableIn: 'Доступно по всему Баку',
          estimatedTime: 'Ориентировочное время доставки',
          openApp: 'Открыть приложение',
          orWeb: 'или посетить сайт'
        }
      },
      
      common: {
        loading: 'Загрузка...',
        error: 'Ошибка',
        success: 'Успех',
        save: 'Сохранить',
        cancel: 'Отменить',
        edit: 'Редактировать',
        delete: 'Удалить',
        confirm: 'Подтвердить',
        close: 'Закрыть',
        back: 'Назад',
        next: 'Далее',
        previous: 'Предыдущий',
        email: 'Email',
        password: 'Пароль',
        name: 'Имя',
        phone: 'Телефон',
        address: 'Адрес',
        submit: 'Отправить',
        required: 'Обязательно',
        optional: 'Необязательно'
      },
      
      forms: {
        firstName: 'Имя',
        lastName: 'Фамилия',
        fullName: 'Полное имя',
        emailAddress: 'Email адрес',
        phoneNumber: 'Номер телефона',
        message: 'Сообщение',
        subject: 'Тема',
        date: 'Дата',
        time: 'Время',
        guests: 'Количество гостей',
        specialRequests: 'Особые пожелания'
      },
      
      admin: {
        dashboard: 'Панель управления',
        orders: 'Заказы',
        menu: 'Управление меню',
        reservations: 'Резервации',
        gallery: 'Галерея',
        contacts: 'Контакты',
        settings: 'Настройки',
        analytics: 'Аналитика',
        users: 'Пользователи',
        reports: 'Отчеты'
      },
      
      footer: {
        contact: 'Контактная информация',
        hours: 'Часы работы',
        social: 'Следите за нами',
        address: 'Адрес',
        phone: 'Телефон',
        email: 'Email',
        addressText: 'Площадь Фонтанов 8, Баку, Азербайджан',
        quickLinks: 'Быстрые ссылки',
        aboutUs: 'О Pizza Inn',
        description: 'Настоящая итальянская пицца в сердце Баку',
        tagline: 'Классика азербайджанской кухни и избранные вкусы мировой кухни.',
        everyday: 'Каждый День'
      }
    }
  },
  
  tr: {
    translation: {
      nav: {
        startOrder: 'Siparişe başla',
        reviewOrder: 'Siparişi incele',
        home: 'Ana Sayfa',
        menu: 'Menü',
        about: 'Hakkımızda',
        gallery: 'Galeri',
        contact: 'İletişim',
        reservations: 'Rezervasyon',
        order: 'Online Sipariş',
        cart: 'Sepet',
        language: 'Dil',
        profile: 'Profil',
        login: 'Giriş',
        logout: 'Çıkış',
        admin: 'Admin'
      },
      
      home: {
        hero: {
          orderNow: 'Şimdi Sipariş Ver',
          viewMenu: 'Menüyü Gör',
          viewMokalandMenu: 'Moka Land Menüsünü Gör',
          eyebrow: 'Fountain Square 8 · Bakü',
          titleLine1: 'Fountain Square,',
          titleLine2: "1995'ten beri.",
          sub: 'Büyük şehrin küçük hikâyesi.',
          seeMenu: 'Menüyü gör →',
          hours: 'Şu an açık · 08:00–03:00',
          location: 'Çeşmeler Meydanı 8, Bakü'
        },
        highlights: {
          awardWinning: 'Ödüllü',
          awardDesc: '692+ memnun müşteriden 4.0★ puanı',
          freshDaily: 'Her Gün Taze',
          freshDailyDesc: 'Premium malzemelerle her gün taze hazırlanır',
          fastDelivery: 'Hızlı Teslimat',
          fastDeliveryDesc: 'Bakü mahallelerine hızlı teslimat'
        },
        businessInfo: {
          hours: 'Çalışma Saatleri:',
          hoursText: 'Her gün 8:00 - 3:00',
          phone: 'Telefon:',
          phoneNumber: '*9777'
        },
        cta: {
          title: 'Gerçek Pizza İster misiniz?',
          subtitle: 'Bakü geneli teslimat için hemen sipariş verin veya Çeşmeler Meydanı\'nda bizi ziyaret edin. Gece 3:00\'e kadar açık!',
          order: 'Online Sipariş',
          menu: 'Menüyü Gör'
        },
        heritage: {
          since: '\'den Beri',
          quote: 'Milli mutfağımızın zengin mirası, dünya mutfağının seçkin lezzetleri. Yılların deneyimi ve ince zevkle her sofrada devam eden bir hikâye.',
        },
        stats: {
          years: 'Bakü\'de Yıllar',
          hours: 'Her Gün Açık',
          locationValue: 'Fıskiye Meydanı',
          location: 'Bakü, Azerbaycan',
        },
        instagram: {
          label: 'Takip Edin',
          title: '@pizzainnbaku',
          body: 'Günlük özel ürünler ve mutfaktan kareler için bizi takip edin.',
          follow: 'Instagram\'da Takip Et',
        },
        delivery: {
          orderVia: 'Teslimat',
          orderDirect: 'Doğrudan Sipariş',
        },
        chapters: {
          eyebrow: 'Pizza Inn',
          title: 'Hareket halinde',
          contentsLabel: 'İçindekiler',
          menuCta: 'Tüm menüyü gör',
          pizza: {
            name: 'Pizza',
            eyebrow: 'Evimiz',
            subject: 'Sıfırdan hazırlanır',
            alt: 'Pizza Inn\'de elle pizza yapımı',
            gloss: 'Fırın, un ve kürek.',
          },
          breakfast: {
            name: 'Kahvaltı',
            eyebrow: 'Sabah',
            subject: 'Kahvaltı sofrada',
            alt: 'Pizza Inn\'de kahvaltı servisi',
            gloss: 'Yumurta, ekmek ve günün ilk sofrası.',
          },
          coffee: {
            name: 'Kahve',
            eyebrow: 'Bar',
            subject: 'Kahve ve kruvasan',
            alt: 'Pizza Inn\'de kahve ve kruvasan',
            gloss: 'Küçük fincanlar, uzun öğleden sonralar.',
          },
          grill: {
            name: 'Izgara',
            eyebrow: 'Akşam',
            subject: 'Izgaradan',
            alt: 'Pizza Inn\'de biftek pişiriliyor',
            gloss: 'Köz, tandır ve paylaşmalık parçalar.',
          },
          garden: {
            name: 'Garden',
            eyebrow: 'Alacakaranlık',
            subject: 'Garden',
            alt: 'Pizza Inn\'in Garden\'ı, içi ve dışıyla',
            gloss: 'Çatısı olmayan oda.',
            body: 'Doğma lezzetler, zengin sofra. Azerbaycan mutfağının en sevilen lezzetleri Garden\'da.',
            cta: 'Garden\'ı ziyaret edin',
          },
          mokaland: {
            name: 'Moka Land',
            eyebrow: 'Aile için',
            subject: 'Eğlencenin en tatlı tadı',
            alt: 'Moka Land\'de oynayan ve yemek yiyen çocuklar',
            gloss: 'Halat, çemberler ve herkes için bir sofra.',
            body: 'Eğlencenin en tatlı tadı Moka Land\'de! Çocuklara eğlence, tüm aileye lezzet.',
            cta: 'Moka Land\'i ziyaret edin',
          },
        },
      },

      about: {
        title: 'Pizza Inn Hakkında',
        subtitle: 'Otantik İtalyan Mutfağı Hikayemiz',
        history: {
          title: 'Tarihimiz',
          content: '1995\'ten beri Pizza Inn, Bakü\'nün kalbinde otantik İtalyan pizzası sunuyor. Küçük bir aile rüyası olarak başlayan şey, şehrin en sevilen yemek mekanlarından birine dönüştü.'
        },
        mission: {
          title: 'Misyonumuz',
          content: 'Geleneksel tarifler, premium malzemeler ve nesiller boyu aktarılan zamana direnen pişirme teknikleri kullanarak size en otantik İtalyan pizza deneyimini getirmeye kendimizi adadık.'
        },
        values: {
          title: 'Değerlerimiz',
          quality: 'Önce Kalite',
          qualityDesc: 'Sadece en iyi malzemeler mutfağımıza girer',
          tradition: 'Geleneksel Yöntemler',
          traditionDesc: 'Otantik İtalyan tarifleri ve pişirme teknikleri',
          service: 'Mükemmel Hizmet',
          serviceDesc: 'Her müşteri geniş ailemizin bir parçasıdır',
          community: 'Toplum Odaklı',
          communityDesc: 'Otuz yıla yakın süredir Bakü toplumuna hizmet etmekten gurur duyuyoruz'
        }
      },
      
      contact: {
        title: 'Bize Ulaşın',
        subtitle: 'İletişimde Kalın',
        getInTouch: 'Sizden haber almaktan memnuniyet duyarız',
        form: {
          title: 'Bize Mesaj Gönderin',
          namePlaceholder: 'Adınız',
          emailPlaceholder: 'E-postanız',
          subjectPlaceholder: 'Konu',
          messagePlaceholder: 'Mesajınız',
          send: 'Mesaj Gönder',
          sending: 'Gönderiliyor...',
          success: 'Teşekkürler! Mesajınız gönderildi. En kısa sürede size dönüş yapacağız.',
          error: 'Mesajınız gönderilemedi. Lütfen tekrar deneyin.',
          networkError: 'Ağ hatası. Bağlantınızı kontrol edip tekrar deneyin.'
        },
        info: {
          title: 'Bizi Ziyaret Edin',
          address: 'Adres',
          addressFull: 'Çeşmeler Meydanı 8, Bakü, Azerbaycan',
          phone: 'Telefon',
          email: 'E-posta',
          hours: 'Çalışma Saatleri',
          hoursWeekdays: 'Pazartesi - Pazar',
          hoursTime: '8:00 - 3:00'
        }
      },
      
      reservations: {
        title: 'Rezervasyon Yapın',
        subtitle: 'Masanızı Ayırtın',
        description: 'Pizza Inn Bakü\'de unutulmaz bir yemek deneyimi için masanızı rezerve edin',
        form: {
          title: 'Rezervasyon Detayları',
          guestsLabel: 'Misafir Sayısı',
          dateLabel: 'Tercih Edilen Tarih',
          timeLabel: 'Tercih Edilen Saat',
          specialRequestsLabel: 'Özel İstekler',
          specialRequestsPlaceholder: 'Özel diyet gereksinimleri veya istekleriniz var mı?',
          reserve: 'Rezervasyon Yap',
          sending: 'Rezervasyon yapılıyor...',
          success: 'Teşekkürler! Rezervasyon talebiniz alındı. En kısa sürede onaylayacağız.',
          error: 'Rezervasyonunuz gönderilemedi. Lütfen tekrar deneyin.',
          networkError: 'Ağ hatası. Bağlantınızı kontrol edip tekrar deneyin.'
        },
        info: {
          title: 'Rezervasyon Bilgileri',
          note1: 'Rezervasyonlar önerilir, özellikle akşam yemeği servisi için',
          note2: 'Büyük gruplar (8+ kişi) önceden rezervasyon gerektirir',
          note3: '30 güne kadar önceden rezervasyon kabul ediyoruz',
          contact: 'Acil yardım için bizi arayın'
        }
      },

      gallery: {
        title: 'Galeri',
        subtitle: 'Yemeklerimizin ve restoranımızın güzel fotoğrafları',
        loading: 'Galeri yükleniyor...',
        empty: 'Henüz fotoğraf yok. Yakında tekrar bakın!',
        loadError: 'Galeri yüklenemedi. Lütfen tekrar deneyin.',
        networkError: 'Ağ hatası. Bağlantınızı kontrol edip tekrar deneyin.',
        retry: 'Tekrar Dene'
      },
      
      menu: {
        title: 'Menümüz',
        eyebrow: 'Fountain Square 8 · Bakü',
        gardenTitle: 'Kış Bahçesi',
        gardenEyebrow: 'Kış Parkı · Bakü',
        welcome: {
          eyebrow: 'Hoş geldiniz',
          body1: "1995'ten bu yana Pizza Inn olarak konuklarımıza keyifli anlar yaşatmak için çalışıyoruz. İster sevdiğiniz klasik lezzetleri tercih edin, ister yeni lezzetler keşfetmek isteyin — sizin için özel olarak hazırlanmış zengin menümüzü sunuyoruz.",
          body2: 'Menümüzde en taze ve kaliteli ürünleri kullanarak hem Azerbaycan mutfağının zengin mirasını hem de uluslararası mutfak örneklerini bir araya getiriyoruz.',
          body3: 'Yemeğinizin ziyaretinizi daha rahat ve lezzetli kılmasını umuyoruz.',
          closing: 'Afiyet olsun!'
        },
        subtitle: 'Gerçek pizzalarımızın ve daha fazlasının lezzetli seçimlerini keşfedin',
        categories: 'Kategoriler',
        search: 'Menü öğelerini ara...',
        addToCart: 'Sepete Ekle',
        viewDetails: 'Detayları Gör',
        price: 'Fiyat',
        ingredients: 'Malzemeler',
        all: 'Tüm Ürünler',
        noResults: 'Aramanızla eşleşen ürün bulunamadı',
        noItems: 'Bu kategoride ürün yok',
        featured: 'Öne Çıkanlar',
        unavailable: 'Mevcut Değil',
        totalItems: 'Toplam Ürünler'
      },

      // Venue page captions
      // Brand menu pages (BrandMenu) and the ALL menu's own strings
      brands: {
        qisparki: {
          seoTitle: 'Pizza Inn Kış Bahçesi - Menü',
          seoDescription: 'Pizza Inn Kış Bahçesi restoran menüsü'
        },
        sedef: {
          eyebrow: 'Sədəf · Bakü',
          title: 'Sedef Milli Mutfak',
          caption: 'Sədəf sofrası: her gün taze hazırlanan lezzetler.',
          seoTitle: 'Sedef Milli Mutfak - Menü',
          seoDescription: 'Sedef Milli Mutfak restoranı menüsü, Bakü',
          serviceCharge: 'Servis ücreti %10',
          photos: 'Fotoğraflar',
          hasPhoto: 'Fotoğraflı'
        }
      },
      allMenu: {
        loadError: 'Menü yüklenemedi. Lütfen tekrar deneyin.'
      },
      qisparki: {
        caption: 'Doğma lezzetler, zengin sofra. Azerbaycan mutfağının en sevilen lezzetleri Garden\'da.'
      },
      mokaland: {
        caption: 'Eğlencenin en tatlı tadı Moka Land\'de! Çocuklara eğlence, tüm aileye lezzet.'
      },

      cart: {
        title: 'Alışveriş Sepeti',
        empty: 'Sepetiniz boş',
        subtotal: 'Ara Toplam',
        serviceFee: 'Servis Ücreti',
        delivery: 'Teslimat Ücreti',
        total: 'Toplam',
        checkout: 'Ödeme',
        remove: 'Kaldır',
        quantity: 'Miktar',
        continueShopping: 'Alışverişe Devam Et',
        proceedToCheckout: 'Ödemeye Geç'
      },
      
      order: {
        title: 'Online Sipariş',
        subtitle: 'Teslimat platformunuzu seçin',
        description: 'Favori teslimat uygulamanız ile Pizza Inn sipariş edin',
        status: 'Sipariş Durumu',
        track: 'Siparişi Takip Et',
        success: 'Sipariş Başarılı',
        failed: 'Sipariş Başarısız',
        pending: 'Bekliyor',
        confirmed: 'Onaylandı',
        preparing: 'Hazırlanıyor',
        ready: 'Hazır',
        delivered: 'Teslim Edildi',
        delivery: {
          wolt: {
            name: 'Wolt',
            action: 'Wolt ile Sipariş Ver',
            description: 'Hızlı ve güvenilir teslimat',
            time: '25-40 dk'
          },
          boltFood: {
            name: 'Bolt Food',
            action: 'Bolt Food ile Sipariş Ver',
            description: 'Kapınıza hızlı teslimat',
            time: '20-35 dk'
          },
          choosePlatform: 'Teslimat Platformunuzu Seçin',
          availableIn: 'Tüm Bakü\'de mevcut',
          estimatedTime: 'Tahmini teslimat süresi',
          openApp: 'Uygulamayı Aç',
          orWeb: 'veya web sitesini ziyaret edin'
        }
      },
      
      common: {
        loading: 'Yükleniyor...',
        error: 'Hata',
        success: 'Başarılı',
        save: 'Kaydet',
        cancel: 'İptal',
        edit: 'Düzenle',
        delete: 'Sil',
        confirm: 'Onayla',
        close: 'Kapat',
        back: 'Geri',
        next: 'İleri',
        previous: 'Önceki',
        email: 'E-posta',
        password: 'Şifre',
        name: 'İsim',
        phone: 'Telefon',
        address: 'Adres',
        submit: 'Gönder',
        required: 'Gerekli',
        optional: 'İsteğe Bağlı'
      },
      
      forms: {
        firstName: 'Ad',
        lastName: 'Soyad',
        fullName: 'Tam Ad',
        emailAddress: 'E-posta Adresi',
        phoneNumber: 'Telefon Numarası',
        message: 'Mesaj',
        subject: 'Konu',
        date: 'Tarih',
        time: 'Saat',
        guests: 'Misafir Sayısı',
        specialRequests: 'Özel İstekler'
      },
      
      admin: {
        dashboard: 'Kontrol Paneli',
        orders: 'Siparişler',
        menu: 'Menü Yönetimi',
        reservations: 'Rezervasyonlar',
        gallery: 'Galeri',
        contacts: 'İletişim',
        settings: 'Ayarlar',
        analytics: 'Analitik',
        users: 'Kullanıcılar',
        reports: 'Raporlar'
      },
      
      footer: {
        contact: 'İletişim Bilgileri',
        hours: 'Çalışma Saatleri',
        social: 'Bizi Takip Edin',
        address: 'Adres',
        phone: 'Telefon',
        email: 'E-posta',
        addressText: 'Çeşmeler Meydanı 8, Bakü, Azerbaycan',
        quickLinks: 'Hızlı Bağlantılar',
        aboutUs: 'Pizza Inn Hakkında',
        description: 'Bakü\'nün kalbinde gerçek İtalyan pizzası',
        tagline: 'Azerbaycan mutfağının klasikleri ve dünya mutfağının seçkin lezzetleri.',
        everyday: 'Her Gün'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'az',
    debug: process.env.NODE_ENV === 'development',
    // i18next only calls missingKeyHandler when saveMissing is on.
    saveMissing: process.env.NODE_ENV === 'development',

    // A missing key is a bug, not a silent fallback: in development,
    // say so instead of letting a chapter quietly fall back to another language.
    missingKeyHandler: process.env.NODE_ENV === 'development'
      ? (lngs, ns, key) => {
          console.warn(`[i18n] missing key "${key}" for ${lngs.join(', ')}`);
        }
      : false,

    interpolation: {
      escapeValue: false
    },
    
    detection: {
      // Disable automatic URL detection since we handle it manually
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'pizzainn_language',
      // Disable path detection since we handle language routing manually
      lookupFromPathIndex: false,
      lookupFromSubdomainIndex: false
    }
  });

export default i18n;
